import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../component/modal/Modal';
import Button from '../../../component/ui/Button';
import searchIcon from '../../../assets/image/search-status.svg';
import { product_list } from '../../../store/action/product';

const AddProduct = ({ isOpen, onClose, onAddProducts, assignedProductIds = [] }) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedIds, setSelectedIds] = useState([]);
	const [availableProducts, setAvailableProducts] = useState([]);
	const [page, setPage] = useState(1);
	const [pageSize] = useState(2000);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);

	const fetchProducts = useCallback(
		async (pageNum, isReset = false) => {
			if (!isOpen) return;
			isReset ? setLoading(true) : setLoadingMore(true);

			try {
				const paramStr = `page=${pageNum}&pageSize=${pageSize}&filterBy=status&filterValue=1`;

				const res = await product_list(paramStr);
				const items =
					res?.data?.productWithStockCount ||
					res?.productWithStockCount ||
					res?.data?.products ||
					res?.products ||
					[];

				const mapped = (items || []).map((p) => ({
					id: p.product_id || p.id,
					name: p.product_name || p.name || '',
					UPC: p.product_code || p.code || p.UPC || p.upc || String(p.product_id || p.id || '-'),
					CATEGORY: p.category_name || p.org_packaging?.name || p.category || p.CATEGORY || 'General',
					'AVAILABLE STOCK': `${p.stock_in_hand ?? 0} ${p.unit_name || 'units'}`,
					'UNIT PRICE': p.unit_price || p.sales_price || '0',
					raw: p,
				}));

				const totalPages = res?.data?.totalPages || res?.totalPages;
				setHasMore(totalPages ? pageNum < Number(totalPages) : items.length === pageSize);

				setAvailableProducts((prev) => {
					if (isReset) return mapped;
					const existingIds = new Set(prev.map((item) => String(item.id)));
					return [...prev, ...mapped.filter((item) => !existingIds.has(String(item.id)))];
				});
			} catch (err) {
				console.error('Failed to load products:', err);
			} finally {
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[isOpen, pageSize]
	);

	useEffect(() => {
		if (isOpen) {
			setPage(1);
			setHasMore(true);
			fetchProducts(1, true);
		}
	}, [isOpen, fetchProducts]);

	const handleScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (scrollHeight - scrollTop - clientHeight <= 80) {
			if (!loading && !loadingMore && hasMore) {
				const nextPage = page + 1;
				setPage(nextPage);
				fetchProducts(nextPage, false);
			}
		}
	};

	const filteredProducts = availableProducts.filter((item) => {
		if (!searchQuery.trim()) return true;
		const query = searchQuery.toLowerCase().trim();
		const nameMatch = item.name ? item.name.toLowerCase().includes(query) : false;
		const codeMatch = item.UPC ? String(item.UPC).toLowerCase().includes(query) : false;
		const categoryMatch = item.CATEGORY ? item.CATEGORY.toLowerCase().includes(query) : false;
		const idMatch = item.id ? String(item.id).toLowerCase().includes(query) : false;
		return nameMatch || codeMatch || categoryMatch || idMatch;
	});

	const handleToggleProduct = (id) => {
		if (assignedProductIds.includes(id)) return;
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
		);
	};

	const handleReset = () => {
		setSearchQuery('');
		setSelectedIds([]);
	};

	const handleClose = () => {
		handleReset();
		if (onClose) onClose();
	};

	const handleAdd = () => {
		const selectedProducts = availableProducts.filter(
			(item) => selectedIds.includes(item.id) && !assignedProductIds.includes(item.id)
		);
		if (onAddProducts) onAddProducts(selectedProducts);
		handleClose();
	};

	return (
		<div data-testid="product-list-add-products-modal">
			<Modal
				className="add-product-modal-wrapper"
				headerClassName="bg-white border-0 pb-0"
				isOpen={isOpen}
				onClose={handleClose}
				cancelButton={handleClose}
				secondaryText={`${selectedIds.length} of ${availableProducts.length} Selected`}
				title="Add Products"
			>
				<div className="add-product-modal-container">
					{/* Search Bar & Reset Row */}
					<div className="d-flex align-items-center gap-2 mb-1">
						<div className="position-relative flex-grow-1">
							<img
								src={searchIcon}
								alt="search"
								className="position-absolute top-50 start-0 translate-middle-y ms-3"
								style={{ width: '16px', height: '16px' }}
							/>
							<input
								type="text"
								className="form-control font-14 rounded-3 ps-5"
								placeholder="Search by product name and product code"
								value={searchQuery}
								data-testid="product-list-add-products-search-input"
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>
						<button
							type="button"
							className="btn text-secondary font-14 fw-semibold p-0 ms-1"
							onClick={handleReset}
							data-testid="product-list-add-products-search-clear"
						>
							Reset
						</button>
					</div>

					{/* Products Items Card Scroll List */}
					<div
						className="add-product-items-list my-2"
						data-testid="product-list-add-products-list"
						onScroll={handleScroll}
					>
						{loading ? (
							<div className="p-4 text-center text-muted font-14">
								<span className="Page_loader" role="status" aria-hidden="true"></span>
							</div>
						) : filteredProducts.length > 0 ? (
							<>
								{filteredProducts.map((item) => {
									const isAlreadyAdded = assignedProductIds.includes(item.id);
									const isChecked = isAlreadyAdded || selectedIds.includes(item.id);
									return (
										<div
											key={item.id}
											data-testid={`product-list-add-products-item-${item.id}`}
											className={`add-product-item-card d-flex align-items-center justify-content-between p-2 px-3 rounded-3 mb-2 ${
												isAlreadyAdded ? 'bg-light opacity-75' : 'cursor-pointer'
											}`}
											onClick={() => {
												if (!isAlreadyAdded) handleToggleProduct(item.id);
											}}
										>
											<div className="d-flex align-items-center gap-3">
												<input
													type="checkbox"
													className="form-check-input mt-0 cursor-pointer"
													checked={isChecked}
													disabled={isAlreadyAdded}
													readOnly
													data-testid={`product-list-add-products-checkbox-${item.id}`}
												/>
												<div>
													<p className="font-14 fw-bold mb-0 text-dark">{item.name}</p>
													<p className="font-12 text-muted mb-0">{item.UPC}</p>
												</div>
											</div>

											{isAlreadyAdded ? (
												<span className="badge bg-secondary-subtle text-secondary px-2 py-1 font-11 fw-semibold rounded-2 ms-2">
													Already Added
												</span>
											) : (
												<span className="badge bg-success-subtle text-success px-2 py-1 font-11 fw-semibold rounded-2 ms-2">
													Available
												</span>
											)}
										</div>
									);
								})}
								{loadingMore && (
									<div className="text-center py-2 font-12 text-muted">
										Loading more products...
									</div>
								)}
							</>
						) : (
							<div className="p-3 text-center text-muted font-14" data-testid="product-list-add-products-no-result-state">
								No products found
							</div>
						)}
					</div>

					{/* Footer Buttons Row */}
					<div className="d-flex justify-content-end gap-2 pt-2 border-top">
						<Button
							type="button"
							button="Cancel"
							className="btn-gray-cancel add-product-footer-btn px-4"
							onClick={handleClose}
							dataTestId="product-list-add-products-cancel-button"
						/>
						<Button
							type="button"
							button="Add"
							className="btn-primary add-product-footer-btn px-4"
							onClick={handleAdd}
							dataTestId="product-list-add-products-save-button"
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default AddProduct;
